/**
 * Tracks genuine user interaction, so the server can enforce an idle timeout
 * that measures idleness instead of guessing at it.
 *
 * The server cannot tell a human apart from a timer: the console polls on its
 * own (dashboard refresh, alert counters, the 45s session heartbeat), so a
 * server-side "last request" clock would keep an abandoned browser signed in
 * indefinitely. The browser is the only party that knows whether someone
 * touched the machine, so it reports it.
 *
 * Every API call carries `X-User-Interaction-Age`: milliseconds since the last
 * real input event. The server bumps its activity stamp only when that age is
 * small, so background polls — which report a large age — move nothing.
 *
 * Deliberately NOT a client-side countdown that logs itself out. A timer in the
 * tab is trivially stopped (background throttling, a sleeping laptop, devtools)
 * and would report a sign-out that never happened. Here the client only ever
 * REPORTS; the server decides and kills the session.
 */

/** Milliseconds since the last real input event, or null if we've seen none. */
let lastInteractionAt: number | null = null;

/**
 * Events that mean a person is present.
 *
 * `pointermove` is included because reading a long page while moving the mouse
 * is not idle, but it fires continuously, so it's throttled hard below — the
 * granularity that matters here is minutes.
 *
 * `visibilitychange` counts only when the tab becomes VISIBLE: switching back
 * to the console is a deliberate act. Becoming hidden is not activity, which is
 * why the handler checks.
 */
const INSTANT_EVENTS = [
  "pointerdown",
  "mousedown",
  "keydown",
  "wheel",
  "touchstart",
  "submit",
] as const;

const THROTTLED_EVENTS = ["pointermove", "mousemove", "scroll"] as const;

// A move/scroll only re-stamps once per this interval. Without it, dragging the
// mouse would run the handler hundreds of times a second for no added accuracy.
const THROTTLE_MS = 5000;
let lastThrottledStamp = 0;

function stamp() {
  lastInteractionAt = Date.now();
}

function stampThrottled() {
  const now = Date.now();
  if (now - lastThrottledStamp < THROTTLE_MS) return;
  lastThrottledStamp = now;
  lastInteractionAt = now;
}

let installed = false;

/** Begin tracking. Idempotent — safe to call from more than one entry point. */
export function startActivityTracking() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // `capture: true` so an interaction is seen even when a component stops
  // propagation, and `passive: true` so listening never delays scrolling.
  const opts = { capture: true, passive: true } as AddEventListenerOptions;
  INSTANT_EVENTS.forEach((e) => window.addEventListener(e, stamp, opts));
  THROTTLED_EVENTS.forEach((e) => window.addEventListener(e, stampThrottled, opts));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") stamp();
  });

  // Loading the app is itself an interaction — somebody navigated here. Without
  // this, the first request after a reload would report "no interaction ever"
  // and the server would measure from session start instead.
  stamp();
}

/**
 * Milliseconds since the last real interaction, or null if none observed.
 * Returned as null rather than a huge number so the server can distinguish
 * "this client doesn't report activity" from "this user has been idle a while".
 */
export function interactionAgeMs(): number | null {
  if (lastInteractionAt === null) return null;
  return Math.max(0, Date.now() - lastInteractionAt);
}

/** Header name; kept beside the tracker so client and server can't drift. */
export const ACTIVITY_HEADER = "X-User-Interaction-Age";
